import { z } from 'zod'

import type {
    CreateSalesReturnRequest,
    ReturnableResponse,
} from '@/lib/api/sales-return-api'
import type { RefundFormValue } from '@/lib/schemas/refund-schemas'
import { emptyRefund } from '@/lib/schemas/refund-schemas'
import { sendsNewLot } from '@/lib/return-new-lot'
import { m } from '@/lib/paraglide/messages'

// M-44 — form popup "Trả hàng" cho đơn đã giao.

const lotSchema = z.object({
    product_lot_id: z.string(),
    lot_code: z.string(),
    returnable_qty: z.number(),
    quantity: z.string(),
    defective: z.boolean(),
    defect_quantity: z.string(),
    position_id: z.string().optional(),
    defect_position_id: z.string().optional(),
    position_new_lot: z.boolean(),
    defect_new_lot: z.boolean(),
    current_position_id: z.string().optional(),
    suggested_position_label: z.string().optional(),
    suggested_defect_position_label: z.string().optional(),
})

const lineSchema = z.object({
    sales_order_item_id: z.string(),
    product_name: z.string(),
    unit: z.string(),
    quantity: z.number(),
    returned_qty: z.number(),
    returnable_qty: z.number(),
    lots: z.array(lotSchema),
})

const packagingSchema = z.object({
    packaging_lot_id: z.string(),
    material_name: z.string(),
    material_unit: z.string(),
    lot_code: z.string(),
    deducted_qty: z.number(),
    enabled: z.boolean(),
    quantity: z.string(),
})

export type SalesReturnLot = z.infer<typeof lotSchema>
export type SalesReturnLine = z.infer<typeof lineSchema>
export type SalesReturnPackaging = z.infer<typeof packagingSchema>
export interface SalesReturnFormValues {
    note: string
    lines: SalesReturnLine[]
    packaging: SalesReturnPackaging[]
    refund: RefundFormValue
    /** Phí trả hàng bên bán giữ lại (chuỗi gõ; rỗng = 0). */
    fee_amount: string
    fee_reason: string
}

/** Số lượng gõ vào; rỗng = 0, sai định dạng = NaN. */
export function qtyOf(raw: string): number {
    return raw.trim() === '' ? 0 : Number(raw)
}

export function lineQty(line: SalesReturnLine): number {
    return line.lots.reduce((sum, l) => {
        const q = qtyOf(l.quantity)
        return Number.isFinite(q) && q > 0 ? sum + q : sum
    }, 0)
}

export function createSalesReturnSchema() {
    return z
        .object({
            note: z.string().max(500),
            lines: z.array(lineSchema),
            packaging: z.array(packagingSchema),
            refund: z.object({
                mode: z.enum(['none', 'full', 'partial']),
                amount: z.string(),
                payment_method: z.enum(['', 'cash', 'bank_transfer']),
            }),
            fee_amount: z.string(),
            fee_reason: z.string().max(500),
        })
        .superRefine((v, ctx) => {
            const fee = qtyOf(v.fee_amount)
            if (!Number.isFinite(fee) || fee < 0) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['fee_amount'],
                    message: m.sales_return_err_fee_invalid(),
                })
            } else if (fee > 0 && v.fee_reason.trim() === '') {
                ctx.addIssue({
                    code: 'custom',
                    path: ['fee_reason'],
                    message: m.sales_return_err_fee_reason(),
                })
            }
            const perLot = new Map<string, number>()
            let total = 0
            v.lines.forEach((line, li) => {
                line.lots.forEach((lot, lj) => {
                    const q = qtyOf(lot.quantity)
                    const base = ['lines', li, 'lots', lj] as const
                    if (!Number.isFinite(q) || q < 0) {
                        ctx.addIssue({
                            code: 'custom',
                            path: [...base, 'quantity'],
                            message: m.sales_return_err_qty_invalid(),
                        })
                        return
                    }
                    if (q > lot.returnable_qty) {
                        ctx.addIssue({
                            code: 'custom',
                            path: [...base, 'quantity'],
                            message: m.sales_return_err_lot_max({
                                max: String(lot.returnable_qty),
                            }),
                        })
                        return
                    }
                    if (q === 0) return
                    total += q
                    const used = (perLot.get(lot.product_lot_id) ?? 0) + q
                    perLot.set(lot.product_lot_id, used)
                    if (used > lot.returnable_qty) {
                        ctx.addIssue({
                            code: 'custom',
                            path: [...base, 'quantity'],
                            message: m.sales_return_err_lot_shared_max({
                                max: String(lot.returnable_qty),
                            }),
                        })
                    }
                    if (lot.defective) {
                        const d = qtyOf(lot.defect_quantity)
                        if (!Number.isFinite(d) || d <= 0 || d > q) {
                            ctx.addIssue({
                                code: 'custom',
                                path: [...base, 'defect_quantity'],
                                message: m.sales_return_err_defect_qty({
                                    max: String(q),
                                }),
                            })
                        }
                        if (!lot.defect_position_id) {
                            ctx.addIssue({
                                code: 'custom',
                                path: [...base, 'defect_position_id'],
                                message:
                                    m.confirm_return_defect_position_required(),
                            })
                        }
                    }
                })
                if (lineQty(line) > line.returnable_qty) {
                    ctx.addIssue({
                        code: 'custom',
                        path: ['lines', li, 'returnable_qty'],
                        message: m.sales_return_err_line_max({
                            max: String(line.returnable_qty),
                        }),
                    })
                }
            })
            if (total === 0) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['lines'],
                    message: m.sales_return_err_empty(),
                })
            }
            v.packaging.forEach((p, i) => {
                if (!p.enabled) return
                const q = qtyOf(p.quantity)
                if (!Number.isFinite(q) || q <= 0 || q > p.deducted_qty) {
                    ctx.addIssue({
                        code: 'custom',
                        path: ['packaging', i, 'quantity'],
                        message: m.sales_return_err_packaging_qty({
                            max: String(p.deducted_qty),
                        }),
                    })
                }
            })
        })
}

/** Giá trị mặc định từ dữ liệu returnable: mọi số trả = 0, vật tư chưa chọn. */
export function toSalesReturnFormValues(
    data: ReturnableResponse,
): SalesReturnFormValues {
    return {
        note: '',
        lines: data.items
            .filter((it) => it.returnable_qty > 0)
            .map((it) => ({
                sales_order_item_id: it.sales_order_item_id,
                product_name: it.product_name,
                unit: it.unit,
                quantity: it.quantity,
                returned_qty: it.returned_qty,
                returnable_qty: it.returnable_qty,
                lots: it.lots
                    .filter((l) => !l.is_virtual && l.returnable_qty > 0)
                    .map((l) => ({
                        product_lot_id: l.product_lot_id,
                        lot_code: l.lot_code,
                        returnable_qty: l.returnable_qty,
                        quantity: '',
                        defective: false,
                        defect_quantity: '',
                        position_id: l.suggested_position_id ?? undefined,
                        defect_position_id:
                            l.suggested_defect_position_id ?? undefined,
                        position_new_lot: false,
                        defect_new_lot: true,
                        current_position_id: l.current_position_id ?? undefined,
                        suggested_position_label: l.suggested_position_label,
                        suggested_defect_position_label:
                            l.suggested_defect_position_label,
                    })),
            })),
        packaging: data.packaging.map((p) => ({
            packaging_lot_id: p.packaging_lot_id ?? '',
            material_name: p.material_name ?? '',
            material_unit: p.material_unit ?? '',
            lot_code: p.lot_code ?? '',
            deducted_qty: p.deducted_qty ?? 0,
            enabled: false,
            quantity: '',
        })),
        refund: emptyRefund(),
        fee_amount: '',
        fee_reason: '',
    }
}

/**
 * Chia số trả của một dòng vào các lô theo thứ tự xuất (FIFO). Phần vượt số còn
 * trả được dồn vào lô cuối để validate báo lỗi, không lặng lẽ cắt bớt.
 */
export function distributeFifo(
    lots: SalesReturnLot[],
    qty: number,
): SalesReturnLot[] {
    let left = Number.isFinite(qty) && qty > 0 ? qty : 0
    return lots.map((l, i) => {
        const take =
            i === lots.length - 1 ? left : Math.min(left, l.returnable_qty)
        left -= take
        return { ...l, quantity: take > 0 ? String(take) : '' }
    })
}

/** Dòng/lô đang trả, cho API xem trước tiền. */
export function toPreviewItems(lines: SalesReturnLine[]) {
    return lines
        .map((l) => ({
            sales_order_item_id: l.sales_order_item_id,
            quantity: lineQty(l),
        }))
        .filter((it) => it.quantity > 0)
}

/** Payload BE (không gồm refund — caller thêm sau khi kiểm với số xem trước). */
export function toCreateReturnPayload(
    v: SalesReturnFormValues,
): CreateSalesReturnRequest {
    const items: CreateSalesReturnRequest['items'] = []
    const restock: NonNullable<CreateSalesReturnRequest['restock_positions']> =
        []
    const defect: NonNullable<CreateSalesReturnRequest['defect_positions']> = []
    const seenGood = new Set<string>()
    const seenDefect = new Set<string>()
    for (const line of v.lines) {
        const lots = line.lots
            .filter((l) => qtyOf(l.quantity) > 0)
            .map((l) => ({
                product_lot_id: l.product_lot_id,
                quantity: qtyOf(l.quantity),
                defect_quantity: l.defective ? qtyOf(l.defect_quantity) : 0,
            }))
        if (lots.length === 0) continue
        items.push({ sales_order_item_id: line.sales_order_item_id, lots })
        for (const l of line.lots) {
            const q = qtyOf(l.quantity)
            if (q <= 0) continue
            const allDefect = l.defective && qtyOf(l.defect_quantity) >= q
            if (
                l.position_id &&
                !allDefect &&
                !seenGood.has(l.product_lot_id)
            ) {
                seenGood.add(l.product_lot_id)
                restock.push({
                    product_lot_id: l.product_lot_id,
                    position_id: l.position_id,
                    new_lot: sendsNewLot(
                        l.position_id,
                        l.current_position_id,
                        l.position_new_lot,
                    ),
                })
            }
            if (
                l.defective &&
                l.defect_position_id &&
                !seenDefect.has(l.product_lot_id)
            ) {
                seenDefect.add(l.product_lot_id)
                defect.push({
                    product_lot_id: l.product_lot_id,
                    position_id: l.defect_position_id,
                    new_lot: sendsNewLot(
                        l.defect_position_id,
                        l.current_position_id,
                        l.defect_new_lot,
                    ),
                })
            }
        }
    }
    const fee = qtyOf(v.fee_amount)
    return {
        note: v.note.trim() || undefined,
        fee_amount: fee > 0 ? fee : 0,
        fee_reason: fee > 0 ? v.fee_reason.trim() : undefined,
        items,
        restock_positions: restock,
        defect_positions: defect,
        packaging_restore_items: v.packaging
            .filter((p) => p.enabled && qtyOf(p.quantity) > 0)
            .map((p) => ({
                packaging_lot_id: p.packaging_lot_id,
                quantity: qtyOf(p.quantity),
            })),
    }
}
